import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";

const REASONS = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "hate", label: "Hate or slurs" },
  { value: "illegal", label: "Illegal content" },
  { value: "other", label: "Other" },
];

/**
 * Report a social post or comment to Circa staff.
 * targetKind: 'post' | 'comment'
 */
export default function SocialReportDialog({
  open,
  onOpenChange,
  accessToken,
  targetKind = "post",
  postId,
  interactionId = null,
}) {
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!accessToken || !postId) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/social/reports`,
        {
          target_kind: targetKind,
          post_id: Number(postId),
          interaction_id:
            targetKind === "comment" && interactionId
              ? Number(interactionId)
              : undefined,
          reason,
          details: details.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Report submitted");
      onOpenChange?.(false);
      setDetails("");
      setReason("spam");
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not submit report"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            Report {targetKind === "comment" ? "comment" : "post"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-2">
            <Label htmlFor="report-reason">Reason</Label>
            <select
              id="report-reason"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-details">Details (optional)</Label>
            <textarea
              id="report-details"
              className="flex min-h-[72px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Anything that helps staff review this"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {busy ? "Submitting…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
